import { decomposeInstrumentKey } from 'modules/BOD/modules/Search/utils';
import { MARKET_FEEDER_MODES } from 'modules/LiveData/constants';
import { cloneDeep } from 'lodash';
import { FeederSubscriptionData } from './typings';
import { DEFAULT_CHANGE_MODE_INSTRUMENT_KEYS } from './constants';

export default class TokenMapper {
  static tokenMapper = new Map<string, FeederSubscriptionData>();

  static chooseMode(modes: FeederSubscriptionData) {
    const feederModes = Object.keys(modes) as MARKET_FEEDER_MODES[];

    if (feederModes.includes(MARKET_FEEDER_MODES.FULL_D30)) {
      return MARKET_FEEDER_MODES.FULL_D30;
    }

    if (feederModes.includes(MARKET_FEEDER_MODES.FULL_SR)) {
      return MARKET_FEEDER_MODES.FULL_SR;
    }

    if (feederModes.includes(MARKET_FEEDER_MODES.FULL)) {
      return MARKET_FEEDER_MODES.FULL;
    }

    if (feederModes.includes(MARKET_FEEDER_MODES.OPTION_CHAIN)) {
      return MARKET_FEEDER_MODES.OPTION_CHAIN;
    }

    if (feederModes.includes(MARKET_FEEDER_MODES.LTPC)) {
      return MARKET_FEEDER_MODES.LTPC;
    }

    return MARKET_FEEDER_MODES.FULL;
  }

  static getFeedModes(instrumentKey: string) {
    if (this.tokenMapper.has(instrumentKey)) {
      return this.tokenMapper.get(instrumentKey);
    }
    return false;
  }

  static addInstrumentKeys(
    instrumentKeys: string[],
    feederMode: MARKET_FEEDER_MODES,
    unsubAndSubAllowed?: boolean,
  ) {
    const subscribedInstrumentKeys: string[] = [];
    const changeModeInstrumentKeys = cloneDeep(
      DEFAULT_CHANGE_MODE_INSTRUMENT_KEYS,
    );

    instrumentKeys.forEach((instrumentKey: string) => {
      if (this.tokenMapper.has(instrumentKey)) {
        const modes = this.tokenMapper.get(instrumentKey);
        const updatedModes: FeederSubscriptionData = {
          ...modes,
          [feederMode]: {
            count: (modes?.[feederMode]?.count ?? 0) + 1,
            unsubAndSubAllowed,
          },
        };
        this.tokenMapper.set(instrumentKey, updatedModes);

        switch (this.chooseMode(updatedModes)) {
          case MARKET_FEEDER_MODES.LTPC:
            changeModeInstrumentKeys.ltpc.push(instrumentKey);
            break;
          case MARKET_FEEDER_MODES.OPTION_CHAIN:
            changeModeInstrumentKeys.option_chain.push(instrumentKey);
            break;
          case MARKET_FEEDER_MODES.FULL:
            changeModeInstrumentKeys.full.push(instrumentKey);
            break;
          case MARKET_FEEDER_MODES.FULL_SR:
            changeModeInstrumentKeys.full_sr.push(instrumentKey);
            break;
          case MARKET_FEEDER_MODES.FULL_D30:
            changeModeInstrumentKeys.full_d30.push(instrumentKey);
            break;
          default:
            break;
        }
      } else if (decomposeInstrumentKey(instrumentKey)?.token !== '0') {
        // Do not subscribe to the token with the value 0 since its a invalid token
        subscribedInstrumentKeys.push(instrumentKey);
        this.tokenMapper.set(instrumentKey, {
          [feederMode]: {
            count: 1,
            unsubAndSubAllowed,
          },
        });
      }
    });

    return { subscribedInstrumentKeys, changeModeInstrumentKeys };
  }

  static removeInstrumentKeys(
    instrumentKeys: string[],
    feederMode: MARKET_FEEDER_MODES,
    unsubAndSubAllowed?: boolean,
  ) {
    const unsubscribedInstrumentKeys: string[] = [];
    const changeModeInstrumentKeys = cloneDeep(
      DEFAULT_CHANGE_MODE_INSTRUMENT_KEYS,
    );

    instrumentKeys.forEach((instrumentKey: string) => {
      if (this.tokenMapper.has(instrumentKey)) {
        const updatedModes = { ...this.tokenMapper.get(instrumentKey) };
        if (updatedModes?.[feederMode]) {
          updatedModes[feederMode] = {
            count: (updatedModes[feederMode]?.count ?? 0) - 1,
            unsubAndSubAllowed:
              unsubAndSubAllowed ??
              updatedModes[feederMode]?.unsubAndSubAllowed,
          };
          // Delete the mode if the count is 0
          if (updatedModes?.[feederMode]?.count === 0) {
            delete updatedModes?.[feederMode];
          }
        }

        if (Object.keys(updatedModes).length === 0) {
          this.tokenMapper.delete(instrumentKey);
          unsubscribedInstrumentKeys.push(instrumentKey);
        } else {
          switch (this.chooseMode(updatedModes)) {
            case MARKET_FEEDER_MODES.LTPC:
              changeModeInstrumentKeys.ltpc.push(instrumentKey);
              break;
            case MARKET_FEEDER_MODES.OPTION_CHAIN:
              changeModeInstrumentKeys.option_chain.push(instrumentKey);
              break;
            case MARKET_FEEDER_MODES.FULL:
              changeModeInstrumentKeys.full.push(instrumentKey);
              break;
            case MARKET_FEEDER_MODES.FULL_SR:
              changeModeInstrumentKeys.full_sr.push(instrumentKey);
              break;
            case MARKET_FEEDER_MODES.FULL_D30:
              changeModeInstrumentKeys.full_d30.push(instrumentKey);
              break;
            default:
              break;
          }
          this.tokenMapper.set(instrumentKey, updatedModes);
        }
      }
    });

    return { unsubscribedInstrumentKeys, changeModeInstrumentKeys };
  }

  static resetTokenMapper() {
    this.tokenMapper.clear();
  }
}
